
import { Component, Prop, Vue } from "vue-property-decorator";
import moment from "moment";
import { formatPrice } from "@/utils/number";

@Component({ name: "PoDetailsHeader" })
export default class extends Vue {
  @Prop({ type: Object, default: () => ({}) }) private info!: Record<string, any>;

  private expanded = true;

  private toggleExpanded() {
    this.expanded = !this.expanded;
    this.$emit("expand-change", this.expanded);
  }

  private get supplierLabel() {
    const abbr = this.info.supplierAbbreviation;
    if (!abbr) {
      return "—";
    }
    return abbr;
  }

  private get roleChips() {
    return [
      { key: "seller", label: "下单销售员", value: this.displayValue(this.info.seller) },
      { key: "productManager", label: "产品经理", value: this.displayValue(this.info.productManager) },
      { key: "orderAuditor", label: "应付会计", value: this.displayValue(this.info.orderAuditor), muted: true },
      {
        key: "productNames",
        label: "产品品名",
        value: this.displayValue(this.info.productNames),
        ellipsis: true,
      },
    ];
  }

  private get deliveryDay() {
    const value = this.info.contractualDeliveryDate;
    if (!value) {
      return null;
    }
    const date = moment(value);
    return date.isValid() ? date.startOf("day") : null;
  }

  private get isCompleted() {
    const status = this.info.status;
    return (
      status === "DELIVERY_COMPLETED" ||
      status === "交货已完成" ||
      status === "已完成" ||
      status === "COMPLETED"
    );
  }

  private get deliveryDays() {
    const start = this.info.createTime
      ? moment(this.info.createTime).startOf("day")
      : null;
    if (!start || !start.isValid() || !this.deliveryDay) {
      return null;
    }
    return this.deliveryDay.diff(start, "days");
  }

  private get orderDays() {
    const end = this.info.completedDeliveryDate
        ? moment(this.info.completedDeliveryDate).startOf("day")
        : null;
    const start = this.info.createTime
        ? moment(this.info.createTime).startOf("day")
        : null;
    const today = moment().startOf("day");
    if (start && end) {
      return  end.diff(start, "days");
    }
    return today.diff(start, "days");
  }

  private get remainingDays() {
    if (this.isCompleted) {
      return "";
    }
    if (!this.deliveryDay) {
      return "-";
    }
    const remaining = this.deliveryDay.diff(moment().startOf("day"), "days");
    if (remaining < 0) {
      return "无";
    }
    return remaining;
  }

  private get overdueDays() {
    if (!this.deliveryDay) {
      return "-";
    }
    const end = this.info.completedDeliveryDate
      ? moment(this.info.completedDeliveryDate).startOf("day")
      : moment().startOf("day");
    if (!end.isValid()) {
      return "-";
    }
    const overdue = end.diff(this.deliveryDay, "days");
    return overdue > 0 ? overdue : "";
  }

  private get deliveryDaysText() {
    const value = this.deliveryDays;
    if (value == null) {
      return "—";
    }
    return `${value}天`;
  }

  private get orderDaysText() {
    const value = this.orderDays;
    if (value == null) {
      return "—";
    }
    return `${value}天`;
  }

  private get remainingDaysText() {
    const value = this.remainingDays;
    if (value === "" || value == null || value === "-") {
      return "—";
    }
    if (value === "无") {
      return "无";
    }
    return `${value}天`;
  }

  private get overdueDaysText() {
    const value = this.overdueDays;
    if (value === "" || value == null || value === "-") {
      return "—";
    }
    return `${value}天`;
  }

  private get remainingClass() {
    const value = this.remainingDays;
    if (value === "无") {
      return "";
    }
    if (typeof value === "number" && value <= 10) {
      return "is-warn";
    }
    if (typeof value === "number" && value > 10) {
      return "is-normal";
    }
    return "";
  }

  private get overdueClass() {
    const value = this.overdueDays;
    return typeof value === "number" && value > 0 ? "is-danger" : "";
  }

  private get deadlineTone() {
    if (this.overdueClass) return "is-overdue";
    if (this.remainingClass === "is-warn") return "is-warn";
    if (this.remainingClass === "is-normal") return "is-normal";
    if (this.isCompleted && !this.overdueClass) {
      return "is-normal";
    }
    return "";
  }

  private get statusBadgeClass() {
    const status = this.info.status;
    if (
      status === "NOT_DELIVERED" ||
      status === "未交货" ||
      status === "TO_BE_UPLOADED_WITH_RECEIPT" ||
      status === "待上传回执" ||
      status === "TO_BE_CONFIRMED_BY_PROCUREMENT" ||
      status === "待采购确认"
    ) {
      return "is-not-delivered";
    }
    if (status === "PARTIALLY_DELIVERED" || status === "部分交货") {
      return "is-partial";
    }
    if (
      status === "DELIVERY_COMPLETED" ||
      status === "交货已完成" ||
      status === "已完成" ||
      status === "COMPLETED"
    ) {
      return "is-completed";
    }
    return "is-unknown";
  }

  private displayValue(value: any) {
    if (value === 0) return "0";
    if (value == null || value === "") return "—";
    return value;
  }

  private priceText(value: any) {
    if (value === "" || value == null || value === "null" || value === "undefined") {
      return "—";
    }
    const amount = Number(value);
    if (Number.isNaN(amount)) {
      return this.displayValue(value);
    }
    const formatted = formatPrice(amount);
    if (!formatted) {
      return "—";
    }
    const negative = formatted.startsWith("-");
    const unsigned = negative ? formatted.slice(1) : formatted;
    const parts = unsigned.split(".");
    const grouped = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    const body = parts.length > 1 ? `${grouped}.${parts[1]}` : grouped;
    return negative ? `-${body}` : body;
  }

  private formatDateTime(value?: string) {
    if (!value) {
      return "—";
    }
    const date = moment(value);
    return date.isValid() ? date.format("YYYY-MM-DD") : value;
  }
}
